import { useState, useEffect, useMemo } from 'react'
import Formsy from 'formsy-react'
import { branch, renderComponent } from 'recompose'

import { withRouter, withTranslate } from 'i18n/utils'
import { useMediaQuery } from 'react-responsive'

import { GRID_BREAKPOINT_SM } from 'shared/breakpoints'
import useBookingValidation from 'shared/hooks/useBookingValidation'
import featureToggles from 'shared/featureToggles'
import { CUSTOMER_TYPE_INDIVIDUAL } from 'shared/constants'

import LoginSignUpContainer from 'shared/components/LoginSignUpContainer'
import ErrorMessage from 'shared/components/ErrorMessage'

import PriceBreakdownModal from 'shared/components/PriceBreakdownModal'
import PaymentMethodSection from 'shared/components/PaymentMethodSection'

import {
    selectedBookingItemSelector,
    ratesPendingSelector,
    guestUserCardVerifiedSelector,
    makePurchasePendingSelector,
    locationDetailsSelector,
} from 'booking/selectors'
import { verifyGuestCreditCardResetState } from 'booking/actions'
import { pseBanksSelector } from 'credit-cards/selectors'
import { getPSEPaymentBanks } from 'credit-cards/actions'

import withRates from '../../../../shared/hocs/withRates'
import withPurchase from '../../../../shared/hocs/withPurchase'
import FooterSection from './FooterSection'
import ProductDetails from './ProductDetails'
import PromoCodeSection from './PromoCodeSection'
import FlightInfo from './FlightInfo'
import PersonalDetails from 'booking/components/GuestCheckoutComponents/PersonalDetails'

import { cancellationSelector, reservationViaUrlSelector } from 'dashboard-transactions/selectors'
import { withRouteParams } from 'shared/hoc'
import { isPaymentSelectionInvalid } from 'shared/transactions'

import bookingStyles from '../styles.module.scss'

const personalDataFormInitialState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    documentType: CUSTOMER_TYPE_INDIVIDUAL,
}

const ReviewPay = ({
    bookingData,
    user,
    rate,
    selectedCreditCard: selectedPaymentMethod, // `selectedCreditCard` here is a generic "selected payment method" (card, PIX, PSE, Google Pay)
    makePurchasePending,
    ratesPending,
    allowPurchase,
    selectedBookingItem,
    onPayForParkingClick,
    setCreditCard,
    selectVehicle,
    selectedVehicle,
    isEventFlow,
    currentItemIndex,
    setOutboundNumber,
    setInboundNumber,
    showPixPaymentMethod,
    guestReservationAmendPolicy,
    guestUserCardIsVerified,
    existingReservation,
    verifyGuestCreditCardResetState,
    getPSEPaymentBanks,
    pseBanks,
    locationDetails,
    analyticsFlowType,
}) => {
    const [priceBreakdownModalOpen, setPriceBreakdownModalOpen] = useState(false)
    const { error, isLoading } = useBookingValidation()
    const personalDataFormRef = React.useRef(null)
    const [outboundNumberError, setOutboundNumberError] = useState(false)
    const [inboundNumberError, setInboundNumberError] = useState(false)
    const [isPersonalDataFormValid, setIsPersonalDataFormValid] = useState(false)
    // guestReservationAmendPolicy has 2 default fields which we don't want to remove
    const guestUserPersonalData = Object.keys(guestReservationAmendPolicy)?.length > 2 && {
        firstName: guestReservationAmendPolicy.parkerFirstName,
        lastName: guestReservationAmendPolicy.parkerLastName,
        email: guestReservationAmendPolicy.parkerEmailAddress,
        phone: guestReservationAmendPolicy.parkerMobile,
    }
    const [personalDataForm, setPersonalDataForm] = React.useState(
        guestUserPersonalData || personalDataFormInitialState
    )

    const userId = user ? user.accountId : null

    // Only guest user can end up here without user object e.g. account
    const isGuest = locationDetails.guestCheckoutEnabled && !user

    const paymentMethods = useMemo(
        () => (user ? user.creditCards : guestUserCardIsVerified ? [guestUserCardIsVerified] : null),
        [user?.creditCards, guestUserCardIsVerified]
    )

    const purchaseError = makePurchasePending instanceof Error ? makePurchasePending : ''

    const discountedTotalPrice =
        selectedBookingItem?.promoCode &&
        selectedBookingItem?.promoCode.statusCode !== -1 &&
        selectedBookingItem?.promoCode.discountTotal

    const showPromoCodeSection = !existingReservation?.reservationId
    const promoCodeBtnDisabled =
        !rate || (ratesPending && !(ratesPending instanceof Error)) || makePurchasePending === true

    const isDesktop = useMediaQuery({ minWidth: GRID_BREAKPOINT_SM })

    const showFlightInfo =
        featureToggles.showFlightInfo && (locationDetails?.isOutboundFlight || locationDetails?.isInboundFlight)

    const flightInfoIsNotFilled =
        featureToggles.showFlightInfo &&
        ((locationDetails?.isOutboundFlight && !bookingData.outboundNumber) ||
            (locationDetails?.isInboundFlight && !bookingData.inboundNumber) ||
            outboundNumberError ||
            inboundNumberError)

    const invalidPaymentSelection = isPaymentSelectionInvalid(selectedPaymentMethod, locationDetails)

    const submitBtnDisabled =
        isLoading || invalidPaymentSelection || flightInfoIsNotFilled || (isGuest && !isPersonalDataFormValid)

    const validatePersonalDataForm = async () => {
        if (personalDataFormRef?.current) {
            await personalDataFormRef.current.validateForm()

            const updatedState = personalDataFormRef.current.state

            setIsPersonalDataFormValid(updatedState.isValid)
        }
    }

    useEffect(() => {
        if (featureToggles.hasPsePurchase) {
            getPSEPaymentBanks()
        }
    }, [])

    useEffect(() => {
        validatePersonalDataForm()
    }, [personalDataForm.documentNumber, personalDataForm.documentType])

    useEffect(() => {
        if (selectedVehicle.length === 0 && user) {
            selectVehicle({ ...bookingData.vehicles[0] })
        }
    }, [user])

    useEffect(() => {
        if (bookingData.outboundNumber?.length === 0) {
            setOutboundNumberError(true)
        } else setOutboundNumberError(false)
    }, [bookingData.outboundNumber])

    useEffect(() => {
        if (bookingData.inboundNumber?.length === 0) {
            setInboundNumberError(true)
        } else setInboundNumberError(false)
    }, [bookingData.inboundNumber])

    const additionalServicePrice = () =>
        bookingData?.additionalServices?.items.reduce((acc, item) => acc + item.value, 0)

    const totalPrice = () => {
        if (additionalServicePrice()) {
            return (
                additionalServicePrice() +
                (discountedTotalPrice != null ? discountedTotalPrice : rate?.amount || bookingData?.event?.amount)
            )
        }
        return discountedTotalPrice != null ? discountedTotalPrice : rate?.amount || bookingData?.product?.amount
    }

    const editedReservationNewTotalPrice = () => {
        const newRatePrice = rate.amount - existingReservation?.amount + guestReservationAmendPolicy?.amendmentFee

        if (newRatePrice !== null) {
            if (newRatePrice > 0) {
                return {
                    amount: newRatePrice,
                    label: <T>booking.total.difference.to.pay.label</T>,
                }
            }
            if (newRatePrice < 0) {
                return {
                    amount: Math.abs(newRatePrice),
                    label: <T>booking.refund.amount.label</T>,
                }
            }
            return { amount: newRatePrice, label: <T>booking.final.price.label</T> }
        }
    }

    const showPriceDifference = Number.isFinite(editedReservationNewTotalPrice()?.amount)

    const onSubmitHandler = () => {
        onPayForParkingClick(
            bookingData?.inboundNumber,
            bookingData?.outboundNumber,
            bookingData?.additionalServices?.value,
            { userPersonalData: personalDataForm, vehicle: bookingData.vehicles[0] },
            existingReservation?.reservationId
        )
    }

    const onChangePersonalDataForm = event => {
        const { name, value } = event.target
        setPersonalDataForm(formData => ({ ...formData, [name]: value }))
        validatePersonalDataForm()
    }

    const removeGuestUserCard = () => {
        verifyGuestCreditCardResetState()
        setCreditCard(null)
    }

    return (
        <div className={isDesktop && 'card-section'}>
            <>
                <div className={!isDesktop && 'card-section'}>
                    <ProductDetails
                        rate={rate}
                        discountedTotalPrice={discountedTotalPrice}
                        startDate={isEventFlow ? bookingData.date.startDate : rate.fromDate}
                        endDate={isEventFlow ? bookingData.date.endDate : rate.toDate}
                        locationName={isEventFlow ? bookingData.event?.eventName : rate?.locationName}
                        vehicles={bookingData.vehicles}
                        additionalServices={bookingData?.additionalServices?.items}
                    />

                    {showPromoCodeSection && (
                        <PromoCodeSection
                            promoCodeBtnDisabled={promoCodeBtnDisabled}
                            rate={rate}
                            currentItemIndex={currentItemIndex}
                        />
                    )}
                </div>
                {priceBreakdownModalOpen && (
                    <PriceBreakdownModal
                        isLoading={makePurchasePending === true}
                        disabled={!allowPurchase}
                        onClose={() => setPriceBreakdownModalOpen(false)}
                        onClick={() => setPriceBreakdownModalOpen(false)}
                        onClickButtonLabel={<T>booking.review.pay.breakdown.modal.confirm.button</T>}
                        error={purchaseError}
                        additionalServices={bookingData?.additionalServices?.items}
                        additionalServicePrice={additionalServicePrice}
                        editedReservationNewTotalPrice={editedReservationNewTotalPrice()}
                        newPrice={rate.amount}
                        oldPrice={existingReservation?.amount}
                        amendmentFee={guestReservationAmendPolicy?.amendmentFee}
                        showPriceDifference={showPriceDifference}
                    />
                )}
                {!user && (
                    <PersonalDetails
                        formData={personalDataForm}
                        onChangeFormData={onChangePersonalDataForm}
                        personalDataFormRef={personalDataFormRef}
                    />
                )}
                {showFlightInfo && (
                    <FlightInfo
                        setOutboundNumber={setOutboundNumber}
                        setInboundNumber={setInboundNumber}
                        bookingData={bookingData}
                        outboundNumberError={outboundNumberError}
                        setOutboundNumberError={setOutboundNumberError}
                        inboundNumberError={inboundNumberError}
                        setInboundNumberError={setInboundNumberError}
                        locationDetails={locationDetails}
                    />
                )}
                <div className={!isDesktop && 'card-section'}>
                    <div className={bookingStyles.paymentMethods}>
                        <PaymentMethodSection
                            selectedPaymentMethod={selectedPaymentMethod}
                            setPaymentMethod={setCreditCard}
                            paymentMethods={paymentMethods}
                            showPixPaymentMethod={showPixPaymentMethod}
                            userId={userId}
                            guestUserFlow={!user}
                            removeGuestUserCard={removeGuestUserCard}
                            pseBanks={pseBanks}
                            hideCreditCardPayment={featureToggles.brNeoPurchaseFlow && !locationDetails?.allowCard}
                            hasGooglePay={featureToggles.hasGooglePayEnabled}
                            analyticsFlowType={analyticsFlowType}
                        />
                    </div>
                    <Formsy onSubmit={onSubmitHandler}>
                        <FooterSection
                            rate={rate || bookingData?.event}
                            disabled={submitBtnDisabled}
                            loading={isLoading}
                            setPriceBreakdownModalOpen={setPriceBreakdownModalOpen}
                            total={totalPrice()}
                            rateCurrencySymbol={rate?.currencySymbol || bookingData?.event?.currencySymbol}
                            editedReservationNewTotalPrice={editedReservationNewTotalPrice()}
                            newPrice={rate.amount}
                            oldPrice={existingReservation?.amount}
                            amendmentFee={guestReservationAmendPolicy?.amendmentFee}
                            showPriceDifference={showPriceDifference}
                            onBuyNowClick={onSubmitHandler}
                        />
                    </Formsy>
                </div>
            </>
            {error && <ErrorMessage className="mt-5" messages={[error]} />}
        </div>
    )
}

export default compose(
    withRouter,
    withRouteParams,
    withTranslate,
    withPurchase,
    connect(
        createStructuredSelector({
            makePurchasePending: makePurchasePendingSelector,
            selectedBookingItem: selectedBookingItemSelector,
            ratesPending: ratesPendingSelector,
            locationDetails: locationDetailsSelector,
            guestReservationAmendPolicy: cancellationSelector,
            existingReservation: reservationViaUrlSelector,
            guestUserCardIsVerified: guestUserCardVerifiedSelector,
            pseBanks: pseBanksSelector,
        }),
        { verifyGuestCreditCardResetState, getPSEPaymentBanks }
    ),
    branch(
        ({ user, locationDetails }) => !user && !locationDetails.guestCheckoutEnabled,
        renderComponent(LoginSignUpContainer),
        renderComponent(ReviewPay)
    )
)(withRates(ReviewPay))
